import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Heart, Sun, MessageCircle, Users, ArrowRight, Star, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const iconMap = {
  Heart: Heart,
  Sun: Sun,
  MessageCircle: MessageCircle,
  Users: Users,
};

export default function Foredrag() {
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/foredrag")
      .then((res) => res.json())
      .then((data) => {
        setLectures(data.items || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching lectures:", err);
        setLoading(false);
      });
  }, []);

  return (
    <>
      <Helmet>
        <title>Foredrag om tabuer, recovery og psykiatri – Ta' JA-Hatten På!</title>
        <meta name="description" content="Book professionelle og erfaringsbaserede foredrag om senfølger efter overgreb, psykiatri, kommunikation og recovery. Se udvalget af foredrag her." />
      </Helmet>

      <section className="pt-32 pb-20 bg-[#fafafa]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 border-b border-red-600 text-red-600 text-xs font-bold tracking-[0.2em] uppercase mb-12"
            >
              <span>Min Ekspertise</span>
            </motion.div>
            <h1 className="text-5xl md:text-7xl font-normal text-slate-900 leading-tight mb-12 tracking-tight">
              Inspiration der <br />
              <span className="italic font-serif text-red-600">rykker grænser</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-light max-w-3xl mx-auto">
              Jeg tilbyder b.la… Et anderledes foredrag, hvor historien bagom, <span className="font-bold text-slate-900">Ta´ JA-Hatten På! Selvindsigt med humor,</span> blev født. Dette oplæg sætter fokus på den menneskelige side af sundhedsvæsenet med humor, selvindsigt og eksempler fra virkeligheden. Et inspirerende oplæg, der giver stof to refleksion for både personale, ledere, studerende, pårørende og alle andre, inklusiv patienter!
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-32">
              <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-px bg-slate-100 border border-slate-100">
              {lectures.map((lecture, i) => {
                const IconComponent = iconMap[lecture.icon_name] || Star;
                return (
                  <motion.div
                    key={lecture.id}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="group bg-white p-12 md:p-16 hover:bg-slate-50 transition-colors"
                  >
                    <div className="mb-10 text-slate-400 group-hover:text-red-600 transition-colors">
                      <IconComponent size={32} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-3xl font-serif italic mb-8 text-slate-900 group-hover:text-red-600 transition-colors">
                      {lecture.title.da}
                    </h3>
                    <p className="text-slate-600 text-base md:text-lg leading-relaxed font-light mb-12">
                      {lecture.description.da}
                    </p>
                    <Link
                      to={`/kontakt?emne=${encodeURIComponent(lecture.title.da)}`}
                      className="inline-flex items-center gap-4 text-slate-900 border-b border-slate-900/10 pb-2 hover:border-red-600 transition-all group/link"
                    >
                      <span className="text-sm font-bold tracking-widest uppercase">Forespørg nu</span>
                      <ArrowRight size={16} className="group-hover/link:translate-x-2 transition-transform text-red-600" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="mt-16 lg:mt-24 py-16 lg:py-20 border-t border-slate-200 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl font-normal text-slate-900 mb-12 tracking-tight leading-tight">
                Har du brug for noget <br />
                <span className="italic font-serif text-red-600">helt specielt?</span>
              </h2>
              <p className="text-lg md:text-xl text-slate-500 mb-16 max-w-2xl mx-auto font-light">
                Jeg skræddersyr gerne et oplæg eller en workshop, der passer præcis til jeres organisation, tema eller udfordring.
              </p>
              <Link
                to="/kontakt"
                className="group relative inline-flex items-center justify-center px-12 py-6 border border-slate-900 text-slate-900 font-semibold tracking-widest uppercase transition-all hover:bg-slate-900 hover:text-white"
              >
                <span>Book et møde</span>
                <ArrowRight className="ml-4 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
